import { ArrowRight, Menu } from "lucide-react";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Separator } from "./ui/separator";
import { ThemeToggle } from "./theme-toggle";
import { LanguageToggle } from "./language-toggle";
import { OptivoWordmark } from "./brand/OptivoWordmark";
import { COMPANY, formattedAddress } from "./legal/companyDetails";
import { PaymentMarks } from "./legal/PaymentMarks";
import { AboutSection } from "./welcome/AboutSection";
import { FeatureBento } from "./welcome/FeatureBento";
import { PricePreview } from "./welcome/PricePreview";
import { PricedRoomsSection } from "./welcome/PricedRoomsSection";
import { PricingSection } from "./welcome/PricingSection";
import { useLanguage } from "../i18n/language-provider";

interface WelcomePageProps {
    onNavigate: (path: "login" | "signup" | "resetPassword") => void;
}

/**
 * In-page sections the header links to, in the order the page runs: who we
 * are, what we do, how it works, what it costs. The ids double as the
 * anchors.
 *
 * Who already uses Optivo is said by one count, the rooms it prices, between
 * how it works and the prices (`PricedRoomsSection`). It has no link in the
 * header: the section is drawn only once the server has given the count, and
 * a link must not lead to a section that may not be there. Until 8 Oct 2026
 * the page named the properties, a partner and the property management
 * systems Optivo connects to; the owners took all three off, a competitor
 * reading this page too.
 *
 * The prices are a section of the page since 5 Oct 2026, and the header's
 * last link goes to it. The price list stays a page of its own: the document
 * the payment gateway reviews, with the trial, the payment and the
 * cancellation beside the prices. The pricing section, the closing band and
 * the footer link to it.
 */
const ABOUT_ID = "about";
const FEATURES_ID = "features";
const HOW_IT_WORKS_ID = "how-it-works";
const PRICED_ROOMS_ID = "priced-rooms";
const PRICING_ID = "pricing";
const GET_STARTED_ID = "get-started";
const PRICE_LIST_PATH = "/subscription";

export function WelcomePage({ onNavigate }: WelcomePageProps) {
    const { t, language } = useLanguage();
    const w = t.welcome;
    const sectionLinks = [
        { label: w.navAbout, href: `#${ABOUT_ID}` },
        { label: w.navFeatures, href: `#${FEATURES_ID}` },
        { label: w.navHowItWorks, href: `#${HOW_IT_WORKS_ID}` },
        { label: w.navPricing, href: `#${PRICING_ID}` },
    ];

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* ── Navigation ─────────────────────────────────── */}
            <header className="@container/welcome-header sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
                {/* gap-3 below lg: at 320px the Czech bar has 11px to spare
                    with it, and stood half a pixel past the edge at gap-6. */}
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-6 lg:gap-6">
                    <div className="flex shrink-0 items-center gap-2">
                        {/* The wordmark names the link: it is `role="img"` with
                            the label "Optivo", the one such image on the page.
                            h-6: Plus Jakarta Sans draws it wider than the serif
                            it replaced, and at h-7 it took the 11px this bar has
                            to spare at 320px in Czech. */}
                        <a href="/" className="shrink-0 rounded-sm">
                            <OptivoWordmark className="h-6 text-primary dark:text-foreground" />
                        </a>

                        {/* Where the bar cannot hold the section links, they fold
                            into this menu: a tablet or a half-width window
                            still reaches every section. A phone's bar has no
                            room for the button either, and keeps the short
                            form it always had. Not modal: a menu of links must
                            not lock the page's scroll under it. */}
                        <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="hidden gap-1.5 text-muted-foreground @min-[42rem]/welcome-header:inline-flex @min-[70rem]/welcome-header:hidden"
                                >
                                    <Menu aria-hidden="true" className="h-4 w-4" />
                                    {w.navMenu}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                {sectionLinks.map((link) => (
                                    <DropdownMenuItem key={link.href} asChild>
                                        <a href={link.href}>{link.label}</a>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    {/* The section links join the bar by the header's own
                        width, never a viewport breakpoint: how much room the
                        labels need depends on the language and the skin's
                        font, and a container query's rem follows the root the
                        skin sets. Measured 3 Oct 2026 in all four skins, with
                        the five links the bar then held: the bar with the
                        links needs up to 68.7rem of header (Better Hotel,
                        Czech; 65rem in the default skin), and the bar with
                        the menu button up to 40rem at the gap it has where
                        the button comes in. 70rem keeps the links on a 1280px
                        laptop with a classic scrollbar, and 42rem puts the
                        menu on a 768px tablet. The bar holds four links since
                        8 Oct 2026 and needs less; the thresholds were left
                        where they were measured. */}
                    <nav
                        aria-label={w.navLabel}
                        className="hidden flex-1 items-center gap-6 text-sm text-muted-foreground @min-[70rem]/welcome-header:flex"
                    >
                        {sectionLinks.map((link) => (
                            <a
                                key={link.href}
                                className="whitespace-nowrap hover:text-foreground transition-colors"
                                href={link.href}
                            >
                                {link.label}
                            </a>
                        ))}
                    </nav>

                    {/* A narrow bar does not fit the long form of this
                        cluster: buttonVariants bakes
                        `whitespace-nowrap shrink-0` into every button and the
                        Separator is `shrink-0` too, so nothing gives and the
                        sticky header pushes "Sign up free" past the right edge,
                        taking the whole document into horizontal scroll. A
                        narrow bar therefore carries the short form — log-in is
                        still reachable twice on the same screen (the hero's
                        `ctaLogIn` and the footer's `logIn`), and the sign-up
                        button keeps its own shorter label rather than a new
                        string.

                        The long form comes in by the header's own width, like
                        the rest of the bar, and its five parts switch
                        together. It used to switch at `sm:`, and a viewport
                        breakpoint sees neither the scrollbar nor the skin's
                        root: under high contrast (a 20px root) the Czech long
                        form needs 654.8px, a classic 17px scrollbar leaves a
                        640px window 623px of header, and "Registrace zdarma"
                        stood 2px past the page's edge. Measured 3 Oct 2026 in
                        all four skins, at the gap the bar has below lg: the
                        long form needs up to 34.4rem of header (Better Hotel,
                        Czech; 33.2rem in the default skin, 32.8rem under high
                        contrast). 36rem is 648px at the 18px root, between
                        the screens: a 640px window keeps the short form
                        whatever its scrollbar takes, and a 667px one has the
                        long form. Under high contrast it is 720px. Below
                        640px the root steps down to 16px, so the long form
                        joins from 576px of header there, where it needs at
                        most 550.5px; high contrast's root is 18px there, and
                        its bar stays short. */}
                    <div className="flex items-center gap-2">
                        <LanguageToggle />
                        {/* The theme switch is the one control the bar can
                            spare, so it steps aside below 20rem of header.
                            That is read off the header rather than the
                            viewport because a container query's rem is the
                            root's: 16px on a phone in every skin but high
                            contrast's 18px. The full bar needs at most 19.8rem
                            (Better Hotel, Czech), so a 320px phone keeps the
                            switch in the 16px skins, while under high contrast
                            it is 17.8rem of header and "Registrace" stood 26px
                            past the edge. */}
                        <ThemeToggle className="@max-[20rem]/welcome-header:hidden" />
                        <Separator
                            orientation="vertical"
                            className="mx-1 hidden h-6 @min-[36rem]/welcome-header:block"
                        />
                        <Button
                            variant="ghost"
                            size="sm"
                            className="hidden @min-[36rem]/welcome-header:inline-flex"
                            onClick={() => onNavigate("login")}
                        >
                            {w.logIn}
                        </Button>
                        <Button size="sm" onClick={() => onNavigate("signup")}>
                            <span className="@min-[36rem]/welcome-header:hidden">{w.signUp}</span>
                            <span className="hidden @min-[36rem]/welcome-header:inline">{w.signUpFree}</span>
                            <ArrowRight className="ml-1 hidden h-3.5 w-3.5 @min-[36rem]/welcome-header:block" />
                        </Button>
                    </div>
                </div>
            </header>

            {/* ── Hero ───────────────────────────────────────────
                The question on the left, the product answering it on the
                right: an example week of the Prices grid whose every price
                explains itself. Stacked, the question reads first. */}
            <section className="mx-auto grid w-full max-w-6xl gap-12 px-6 pt-12 pb-16 sm:pt-16 lg:grid-cols-12 lg:items-center lg:gap-10 lg:pt-20 lg:pb-24">
                <div className="lg:col-span-5">
                    {/* The mark is a plain dot in the emphasis colour, not a
                        glyph: a sparkle reads as "AI", which the product does
                        not claim. */}
                    <span className="inline-flex items-center gap-2 rounded-full border border-emphasis/30 bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
                        <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-emphasis" />
                        {w.badge}
                    </span>

                    {/* The emphasis starts a line of its own, so the bronze
                        phrase is never split across two. It stays upright:
                        display type is emphasised by colour, not a slant
                        (`.type-display em` in globals.css). The size tops out
                        where the bronze half still fits the hero column on
                        one line (491px from 1280px up), which is why the
                        question breaks before its last two words in Czech and
                        after its comma in English. The plain half wraps
                        `pretty`, so no word is left alone on a line. */}
                    <h1 className="type-display mt-6 text-[clamp(2.5rem,5vw,2.9rem)]">
                        <span className="block text-pretty">{w.heroTitle1}</span>{" "}
                        <em className="block text-emphasis">{w.heroTitle2}</em>
                    </h1>

                    {/* The answer to the question: what Optivo is, in one line. */}
                    <p className="type-display-section mt-5 text-xl text-balance sm:text-2xl">{w.heroTagline}</p>

                    <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                        {w.heroDescription}
                    </p>

                    {/* ── Primary CTAs ──────────────────────────── */}
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                        <Button size="lg" className="h-12 px-6 text-base" onClick={() => onNavigate("signup")}>
                            {w.ctaCreate}
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="lg"
                            className="h-12 px-6 text-base"
                            onClick={() => onNavigate("login")}
                        >
                            {w.ctaLogIn}
                        </Button>
                    </div>

                    <p className="mt-4 text-sm text-muted-foreground">{w.ctaSubtext}</p>

                    <Button
                        variant="link"
                        size="sm"
                        className="mt-1 h-auto px-0 text-muted-foreground"
                        onClick={() => onNavigate("resetPassword")}
                    >
                        {w.resetPassword}
                    </Button>
                </div>

                <div className="lg:col-span-7">
                    <PricePreview />
                </div>
            </section>

            {/* ── Who we are ─────────────────────────────────── */}
            <AboutSection id={ABOUT_ID} />

            {/* ── What we do ─────────────────────────────────── */}
            <section id={FEATURES_ID} className="scroll-mt-20 border-t">
                <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24">
                    <div className="max-w-2xl">
                        <p className="type-eyebrow">{w.navFeatures}</p>
                        <h2 className="type-display-section mt-3 text-3xl text-balance">{w.valuePropTitle}</h2>
                        <p className="mt-4 leading-relaxed text-muted-foreground">{w.valuePropDescription}</p>
                    </div>

                    <div className="mt-12">
                        <FeatureBento />
                    </div>
                </div>
            </section>

            {/* ── How it works ───────────────────────────────── */}
            <section id={HOW_IT_WORKS_ID} className="scroll-mt-20 border-t bg-card/60">
                <div className="mx-auto max-w-6xl px-6 py-20 lg:py-24">
                    <p className="type-eyebrow">{w.navHowItWorks}</p>
                    <h2 className="type-display-section mt-3 text-3xl">{w.howItWorksTitle}</h2>

                    <ol className="mt-12 grid gap-10 md:grid-cols-3 md:gap-8">
                        <Step number="01" title={w.step1Title} description={w.step1Desc} />
                        <Step number="02" title={w.step2Title} description={w.step2Desc} />
                        <Step number="03" title={w.step3Title} description={w.step3Desc} />
                    </ol>
                </div>
            </section>

            {/* ── How many rooms Optivo prices ───────────────────
                The one thing the page says about who uses Optivo: a count
                from the database, and nobody's name. Drawn only once the
                server has given the count. */}
            <PricedRoomsSection id={PRICED_ROOMS_ID} />

            {/* ── What it costs ──────────────────────────────── */}
            <PricingSection
                id={PRICING_ID}
                priceListHref={PRICE_LIST_PATH}
                onSignUp={() => onNavigate("signup")}
            />

            {/* ── The way in, and what it costs ──────────────────
                The page closes on its own question, with the two answers a
                visitor has left to look for side by side. Plain since
                5 Oct 2026: the prices above it took the toned band, and two
                bands of one tone read as one section with a rule through
                it. */}
            <section id={GET_STARTED_ID} className="scroll-mt-20 border-t">
                <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 py-16 lg:flex-row lg:items-center lg:justify-between lg:py-20">
                    <div className="max-w-2xl">
                        <h2 className="type-display-section text-3xl text-balance">{w.closingTitle}</h2>
                        <p className="mt-3 leading-relaxed text-muted-foreground">{w.closingDescription}</p>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Button size="lg" className="h-12 px-6 text-base" onClick={() => onNavigate("signup")}>
                            {w.ctaCreate}
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                        <Button asChild variant="outline" size="lg" className="h-12 px-6 text-base">
                            <a href={PRICE_LIST_PATH}>{w.navPricing}</a>
                        </Button>
                    </div>
                </div>
            </section>

            <Separator />

            {/* ── Footer ─────────────────────────────────────────
                The payment gateway reviews this footer logged out: it is
                where GoPay's checklist expects the merchant's identification
                ("ideálně je vložte do patičky webu"), a reachable e-mail and
                phone, and the accepted card marks. Everything below is one of
                those requirements — see doc/features/legal-pages.md. */}
            <footer className="mx-auto w-full max-w-6xl px-6 py-10 text-sm text-muted-foreground">
                <div className="flex flex-col gap-6 sm:flex-row sm:justify-between">
                    <div className="space-y-1">
                        <OptivoWordmark decorative className="mb-3 h-6 text-primary dark:text-foreground" />
                        <p className="text-foreground">{COMPANY.legalName}</p>
                        <p>
                            {w.footerCompanyId} {COMPANY.ico} · {formattedAddress(language)}
                        </p>
                        <p>
                            <a className="hover:text-foreground transition-colors" href={`mailto:${COMPANY.email}`}>
                                {COMPANY.email}
                            </a>{" "}
                            · {COMPANY.phone}
                        </p>
                    </div>

                    {/* This is the nav the gateway reviewer taps to reach
                        /terms, /complaints and /privacy on a phone, so each
                        link needs a real hit box: the text alone is a 19px
                        target with 19px targets on the rows either side of it.
                        `min-h-10` grows the box without moving the baseline or
                        drawing anything, and the `sm:` resets hold desktop at
                        exactly today's density. */}
                    <nav className="flex flex-wrap gap-x-4 gap-y-1 sm:gap-y-2 sm:justify-end [&>*]:inline-flex [&>*]:items-center [&>*]:min-h-10 sm:[&>*]:min-h-0">
                        <a className="hover:text-foreground transition-colors" href={PRICE_LIST_PATH}>
                            {w.footerPricing}
                        </a>
                        <a className="hover:text-foreground transition-colors" href="/payment">
                            {w.footerPayment}
                        </a>
                        <a className="hover:text-foreground transition-colors" href="/terms">
                            {w.footerTerms}
                        </a>
                        <a className="hover:text-foreground transition-colors" href="/complaints">
                            {w.footerComplaints}
                        </a>
                        <a className="hover:text-foreground transition-colors" href="/contact">
                            {w.footerContact}
                        </a>
                        <a className="hover:text-foreground transition-colors" href="/privacy">
                            {w.footerPrivacy}
                        </a>
                        <button
                            className="hover:text-foreground transition-colors"
                            onClick={() => onNavigate("login")}
                        >
                            {w.logIn}
                        </button>
                        <button
                            className="hover:text-foreground transition-colors"
                            onClick={() => onNavigate("signup")}
                        >
                            {w.signUp}
                        </button>
                    </nav>
                </div>

                <Separator className="my-6" />

                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <span>
                        {w.footerCopyright.replace("{year}", String(new Date().getFullYear()))}
                    </span>
                    <PaymentMarks />
                </div>
            </footer>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Step({
    number,
    title,
    description,
}: {
    number: string;
    title: string;
    description: string;
}) {
    return (
        <li className="border-t border-foreground/80 pt-5">
            <span aria-hidden="true" className="type-display block text-4xl text-emphasis">
                {number}
            </span>
            <h3 className="type-display-section mt-4 text-xl">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
        </li>
    );
}
